import { graphFetch } from './client';

/**
 * Entra directory search for the admin people/group picker.
 *
 * These calls request their scopes incrementally (per-call), so the rest of
 * the app never depends on them: if User.ReadBasic.All / Group.Read.All
 * haven't been admin-consented yet, only directory search fails (with a 403
 * or consent error) and the UI falls back to manual entry.
 */

export const USER_SEARCH_SCOPES = ['User.ReadBasic.All'];
export const GROUP_SEARCH_SCOPES = ['Group.Read.All'];

export interface DirectoryUser {
  id: string;
  displayName: string;
  userPrincipalName: string;
}

export interface DirectoryGroup {
  id: string;
  displayName: string;
}

/** $search terms are quoted phrases; strip characters that would break out. */
export function sanitizeSearchTerm(query: string): string {
  return query.replace(/["\\]/g, ' ').replace(/\s+/g, ' ').trim();
}

export async function searchUsers(query: string): Promise<DirectoryUser[]> {
  const q = sanitizeSearchTerm(query);
  if (!q) return [];
  const search = encodeURIComponent(
    `"displayName:${q}" OR "userPrincipalName:${q}"`,
  );
  const res = await graphFetch<{ value: DirectoryUser[] }>(
    `/users?$search=${search}&$select=id,displayName,userPrincipalName&$top=10&$count=true`,
    {
      headers: { ConsistencyLevel: 'eventual' },
      scopes: USER_SEARCH_SCOPES,
    },
  );
  return res.value;
}

/**
 * Resolve one UPN to a display name — best-effort: null on any failure
 * (unknown user, missing consent), callers fall back to the UPN itself.
 */
export async function getUserDisplayName(upn: string): Promise<string | null> {
  const clean = upn.trim();
  if (!clean || !clean.includes('@')) return null;
  try {
    const res = await graphFetch<{ displayName: string }>(
      `/users/${encodeURIComponent(clean)}?$select=displayName`,
      { scopes: USER_SEARCH_SCOPES },
    );
    return res.displayName || null;
  } catch {
    return null;
  }
}

export async function searchGroups(query: string): Promise<DirectoryGroup[]> {
  const q = sanitizeSearchTerm(query);
  if (!q) return [];
  const search = encodeURIComponent(`"displayName:${q}"`);
  const res = await graphFetch<{ value: DirectoryGroup[] }>(
    `/groups?$search=${search}&$filter=securityEnabled eq true&$select=id,displayName&$top=10&$count=true`,
    {
      headers: { ConsistencyLevel: 'eventual' },
      scopes: GROUP_SEARCH_SCOPES,
    },
  );
  return res.value;
}
